import { ApiService } from '../services/apiService';
import { Store } from '../store/Store';
import { withRouter } from '../routes/withRouter';
import { ChangeEvent, createRef, useContext, useEffect, useState } from 'react';

import FormSection from './FormSection';
import FormInput from './FormInput';
import Form from './Form';

const TableSync = (props: any) => {
  const store = useContext(Store).store;
  const formRef = createRef<any>();

  const [credentials, setCredentials] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [synchronising, setSynchronising] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errorTop, setErrorTop] = useState(0);
  const [errorSticky, setErrorSticky] = useState(false);
  const [hideError, setHideError] = useState(false);

  const getErrorMessage = (error: any, context: string): string => {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return `<strong>Connection Error:</strong> Unable to connect to the server. Please check your internet connection and try again.`;
    }

    // Handle API errors with statusCode
    if (error?.statusCode) {
      switch (error.statusCode) {        case 400:
          if (context === 'database credentials') {
            return `<strong>Invalid Database Credentials:</strong> Please check that your database host, database name, schema, username, and password are correct. Common issues:<br/>
            • Host address may be incorrect or unreachable<br/>
            • Database name does not exist<br/>
            • Schema name is incorrect<br/>
            • Username or password is invalid<br/>
            • Database server is not accepting connections`;
          }
          if (context === 'database synchronisation') {
            return `<strong>Synchronisation Failed:</strong> Cannot synchronise with the database. Please ensure your database credentials are valid and the database is accessible.`;
          }
          return `<strong>Invalid Request:</strong> ${error.message || 'Please check your input and try again.'}`;
        case 401:
          return `<strong>Authentication Failed:</strong> The database credentials are invalid. Please verify your username and password.`;
        case 403:
          return `<strong>Access Denied:</strong> The user does not have permission to access this database or schema.`;
        case 404:
          return `<strong>Database Not Found:</strong> The specified database or schema does not exist. Please check the database name and schema.`;
        case 500:
          if (context === 'database synchronisation') {
            return `<strong>Synchronisation Error:</strong> There was an internal error during database synchronisation. Please ensure your database credentials are correct and try again.`;
          }
          return `<strong>Database Server Error:</strong> There was an internal error connecting to your database. Please check that your database server is running and accessible.`;
        case 502:
        case 503:
        case 504:
          return `<strong>Database Unavailable:</strong> The database server is currently unavailable. Please try again later or check your database server status.`;
        default:
          return `<strong>Error (${error.statusCode}):</strong> ${error.message || 'An unexpected error occurred. Please try again.'}`;
      }
    }

    // Handle API errors with message
    if (error?.message) {
      // Check for common database connection error patterns
      const message = error.message.toLowerCase();
      if (message.includes('timeout') || message.includes('timed out')) {
        return `<strong>Connection Timeout:</strong> The database server is taking too long to respond. Please check your host address and ensure the database server is accessible.`;
      }
      if (message.includes('connection refused') || message.includes('host unreachable')) {
        return `<strong>Connection Refused:</strong> Cannot connect to the database host. Please verify the host address and port are correct.`;
      }
      if (message.includes('authentication failed') || message.includes('login failed')) {
        return `<strong>Authentication Failed:</strong> Invalid username or password. Please check your database credentials.`;
      }
      if (message.includes('database') && message.includes('not exist')) {
        return `<strong>Database Not Found:</strong> The specified database does not exist. Please check the database name.`;
      }
      if (message.includes('schema') && message.includes('not exist')) {
        return `<strong>Schema Not Found:</strong> The specified schema does not exist. Please check the schema name.`;
      }
      
      return `<strong>Database Error:</strong> ${error.message}`;
    }

    // Handle string errors
    if (typeof error === 'string') {
      return `<strong>Error:</strong> ${error}`;
    }    // Default fallback
    if (context === 'database credentials') {
      return `<strong>Database Connection Error:</strong> Unable to save or validate database credentials. Please verify all fields are correct and that your database server is accessible.`;
    }
    if (context === 'database synchronisation') {
      return `<strong>Synchronisation Error:</strong> Unable to synchronise with your database. Please ensure your database credentials are valid and try again.`;
    }
    return `<strong>Unexpected Error:</strong> An unknown error occurred. Please try again or contact support if the problem persists.`;
  };

  const onScroll = () =>
    formRef?.current &&
    setErrorTop(formRef.current.getBoundingClientRect().top);

  const onErrorHideClick = () => {
    setHideError(true);
    setTimeout(() => {
      setHideError(false);
      setError('');
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  });

  useEffect(() => setErrorSticky(errorTop < 0), [errorTop]);

  useEffect(() => {
    getDatabaseCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.organisationSelected?.organisationId]);

  const getDatabaseCredentials = async () => {
    const credentials = await ApiService.getInstance().getDatabaseCredentials(
      store?.organisationSelected?.organisationId
    );

    setCredentials(credentials);
  };  const onCredentialsChange = (
    event: ChangeEvent<HTMLInputElement>,
    property: string
  ) => {
    // Clear any existing errors or success messages when user starts typing
    if (error) {
      setError('');
    }
    if (success) {
      setSuccess('');
    }
    
    setCredentials({
      ...credentials,
      [property]: event.currentTarget.value,
    });
  };const validateCredentials = (): boolean => {
    if (!credentials.host?.trim()) {
      setError('<strong>Validation Error:</strong> Host is required. Please enter your database host address.');
      return false;
    }
    if (!credentials.database?.trim()) {
      setError('<strong>Validation Error:</strong> Database name is required. Please enter your database name.');
      return false;
    }
    if (!credentials.schema?.trim()) {
      setError('<strong>Validation Error:</strong> Schema is required. Please enter your database schema name.');
      return false;
    }
    if (!credentials.user?.trim()) {
      setError('<strong>Validation Error:</strong> Username is required. Please enter your database username.');
      return false;
    }
    if (!credentials.password?.trim()) {
      setError('<strong>Validation Error:</strong> Password is required. Please enter your database password.');
      return false;
    }
    return true;
  };
  const saveCredentials = async () => {
    if (!validateCredentials()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await ApiService.getInstance().setDatabaseCredentials(
        store.organisationSelected.organisationId,
        credentials.host,
        credentials.database,
        credentials.schema,
        credentials.password,
        credentials.user
      );
      setSuccess('Database credentials saved successfully!');
    } catch (error: any) {
      setError(getErrorMessage(error, 'database credentials'));
    }

    setLoading(false);
  };  const synchroniseDatabase = async () => {
    if (!validateCredentials()) {
      return;
    }

    setLoading(true);
    setSynchronising(true);
    setError('');
    setSuccess('');

    try {
      await ApiService.getInstance().tableSyncByOrganisationId(
        store.organisationSelected?.organisationId
      );
      setSuccess('Database synchronisation completed successfully!');
    } catch (error: any) {
      setError(getErrorMessage(error, 'database synchronisation'));
    }

    setLoading(false);
    setSynchronising(false);
  };

  return (
    <>
    {/* Add a sub heading before Database credentials and a note to explain when to Sync */}
      <div className="page-sub-heading">
        <h4>Database Synchronisation</h4>
      </div>
      <div className="col-12 col-sm-10 col-lg-10 col-xl-6 mb-4">
        <div className="mb-4 text-dark fs-8">
          If you have made changes to your Areas, Subareas, Versions or Helper Tables, you can synchronise so that these names appear in your database for reporting.
        </div>
      </div>
      {/* Add the synchronise button here */}
      <div className="col-12 col-sm-10 col-lg-10 col-xl-6 mb-4">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={synchroniseDatabase}
          disabled={loading}
        >
          {synchronising ? 'Synchronising...' : 'Synchronise to your database'}
        </button>
      </div>
      <div className="page-sub-heading">
        <h4>Database Credentials</h4>
      </div>
      <div className="col-12 col-sm-10 col-lg-10 col-xl-6">        <div className="mb-4 text-dark fs-8">
          These credentials are for the entire organisation and not per user.
          These SQL credentials will be used to write data to your database.
          <br /><br />
          <strong>Common database settings:</strong>
          <ul className="mb-0 mt-2">
            <li><strong>Host:</strong> Your database server address (e.g., localhost, server.database.windows.net)</li>
            <li><strong>Database:</strong> The name of your database</li>
            <li><strong>Schema:</strong> Database schema name (e.g., dbo for SQL Server)</li>
            <li><strong>Username:</strong> Database user with read/write permissions</li>
            <li><strong>Password:</strong> Password for the database user</li>
          </ul>
        </div><div className="card card-sm">
          <div className="card-body" ref={formRef}>
            {error && (
              <div
                className={`error-message ${hideError ? 'hide' : ''} ${
                  errorSticky ? 'sticky' : ''
                }`}
              >
                <span>
                  <div dangerouslySetInnerHTML={{ __html: error }}></div>
                  <i
                    className="material-symbols-rounded"
                    onClick={onErrorHideClick}
                  >
                    cancel
                  </i>
                </span>
              </div>
            )}
            {success && (
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                <strong>Success!</strong> {success}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess('')}
                  aria-label="Close"
                ></button>
              </div>
            )}            <Form onSubmit={() => {}}>
              <FormSection>
                <FormInput
                  id="host"
                  placeholder="host"
                  label="Host"
                  value={credentials.host}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onCredentialsChange(event, 'host')
                  }
                />
                <div className="row g-3" style={{ marginBottom: '1rem' }}>
                  <div className="col-md-6">
                    <div style={{ width: '100%' }}>
                      <FormInput
                        id="database"
                        placeholder="database"
                        label="Database"
                        value={credentials.database}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onCredentialsChange(event, 'database')
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ width: '100%' }}>
                      <FormInput
                        id="schema"
                        placeholder="schema"
                        label="Schema"
                        value={credentials.schema}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onCredentialsChange(event, 'schema')
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="row g-3" style={{ marginBottom: '1rem' }}>
                  <div className="col-md-6">
                    <div style={{ width: '100%' }}>
                      <FormInput
                        id="user"
                        placeholder="user"
                        label="Username"
                        value={credentials.user}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onCredentialsChange(event, 'user')
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div style={{ width: '100%' }}>
                      <FormInput
                        type="password"
                        id="password"
                        placeholder="password"
                        label="Password"
                        value={credentials.password}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onCredentialsChange(event, 'password')
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-content-end gap-2 mt-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={synchroniseDatabase}
                  >
                    Synchronise
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={saveCredentials}
                  >
                    Save
                  </button>
                </div>
              </FormSection>
            </Form>
            {loading && (
              <div className="card-loader">
                {synchronising && <span>Synchronising</span>}
                <svg
                  className="spinner primary"
                  width="50px"
                  height="50px"
                  viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    className="circle"
                    fill="none"
                    strokeWidth="6"
                    strokeLinecap="round"
                    cx="33"
                    cy="33"
                    r="30"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
        <p style={{ marginBottom: '40px' }}> </p>
        <div>
          <h4>Step 1 | Download our manifest.xml file</h4>
          <p style={{ marginBottom: '40px' }}>
            Download this manifest file before proceeding to step 2:{' '}
            <a href="/ExcelConnectorManifest.xml" download>
              ExcelConnectorManifest.xml
            </a>
          </p>
        </div>
        <div>
          <h4>
            Step 2 | Add the Excel Connector to your Microsoft 365 environment
          </h4>
          <p style={{ marginBottom: '20px' }}>
            This video shows you how to add our Excel Connector to your
            Microsoft 365 environment.
          </p>
        </div>
        <div
          style={{
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            overflow: 'hidden',
            maxWidth: '100%',
            background: '#000',
          }}
        >
          <iframe
            src="https://www.youtube.com/embed/WaaNdvmlbuU"
            title="AdminPortal"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
        <div>
          <h4>Step 3 | Add the Excel Connector to your Microsoft Excel 365</h4>
          <p style={{ marginBottom: '20px' }}>
            This video shows you how to add our Excel Connector to your
            Microsoft 365 environment.
          </p>
        </div>
        <div
          style={{
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            overflow: 'hidden',
            maxWidth: '100%',
            background: '#000',
          }}
        >
          <iframe
            src="https://www.youtube.com/embed/3Q7ClQy-Xnw"
            title="Microsoft365Excel"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </div>
      </div>
    </>
  );
};

export default withRouter(TableSync);
